<!-- pdf转图片/提取pdf图片 -->
<script>
import PkcIcon from '../pkc-icon.vue'
import { debounce } from 'lodash-es'
import { DEFAULT_SCALE } from './js/index'
import { validateFile } from './js/utils'
import PDFPogressor from './js/pdf-converter'

export default {
  name: 'PdfToImage',
  components: { PkcIcon },
  data() {
    return {
      loadingMap: {
        upload: false, // 上传
        resolve: false // 解析
      },
      visible: false,
      progressing: {
        total: 0,
        current: 0
      },
      filename: '',
      imageQuality: DEFAULT_SCALE // 图片缩放，1倍为标清，2倍为高清
    }
  },
  computed: {
    isProgressing() {
      return this.progressing.total > 0 && this.progressing.current < this.progressing.total
    }
  },
  watch: {
    visible: debounce(function (val) {
      if (val) {
        PDFPogressor.init()
      } else {
        this.clear(true)
      }
    })
  },
  beforeDestroy() {
    // 销毁
    this.clear(true)
  },
  created() {
    this.pdf = null
    this.filename = ''
  },
  methods: {
    init() {
      this.clear(false)
      if (!this.visible) return
      PDFPogressor.init()
    },
    clearProgressing() {
      this.progressing.current = 0
      this.progressing.total = 0
    },
    // 清理pdf
    clearPdf() {
      if (this.pdf) {
        this.pdf.destroy()
        this.pdf = null
      }
    },
    clearFiles() {
      if (this.$refs.pdfUploader && this.$refs.pdfUploader.clearFiles) {
        this.$refs.pdfUploader.clearFiles()
      }
    },
    clear(clearWorker = false) {
      PDFPogressor.terminate(null, { clearWorker })
      this.clearFiles()
      this.clearPdf()
      this.clearProgressing()
    },
    async handleChange(file) {
      // 清理之前的状态
      this.filename = ''
      this.clearProgressing()
      if (!file || !file.raw) return
      const rawFile = file.raw
      try {
        validateFile(rawFile)
      } catch (error) {
        this.clearFiles()
        this.$message.error(`处理失败：${error ? error.message : '未知错误'}`)
        return
      }
      try {
        this.filename = rawFile.name
        this.pdf = await PDFPogressor.getPDF(rawFile)
        this.progressing.total = this.pdf.numPages
        // 解析后再注册事件，避免PDFPogressor未初始化完成
        PDFPogressor.on('pageComplete', (page) => {
          this.progressing.current = page
        })
        await PDFPogressor.getImages({
          pdf: this.pdf,
          zip: true,
          callback: () => {
            this.clearFiles()
            this.clearPdf()
            this.$message.success('处理完成，开始下载图片压缩文件')
          }
        })
        // 清理上传的文件
      } catch (error) {
        this.$message.error(`处理失败：${error ? error.message : '未知错误'}`)
      }
    },
    handleRemove() {
      this.clear()
    },
    // 设置图片缩放
    setImageQuality(value = 1) {
      if (!PDFPogressor.setImageScale) return
      PDFPogressor.setImageScale(value)
    }
  }
}
</script>

<template>
  <div class="pdf-to-image">
    <div v-show="!visible" class="pdf-to-image__trigger" @click="() => (visible = true)">
      <slot>
        <PkcIcon icon="#d2-pdf-convert" width="30" height="30" />
      </slot>
    </div>
    <el-dialog
      title="PDF转图片"
      :visible.sync="visible"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      width="600px"
      append-to-body
      class="el-dialog__wrapper_center"
      custom-class="ly-dialog--v3 dialog-flexable--column"
    >
      <div class="pdf-to-image__content">
        <div class="pdf-to-image__tip d2-mb-8">
          <span>
            转化过程中<span style="color: var(--color-danger)"> 请勿关闭弹窗 </span>
            ，否则转化将中断。
          </span>
          转化完成后，自动下载图片压缩文件。
        </div>
        <div class="description-row d2-mb-8" data-label="图片压缩：">
          <el-select
            v-model="imageQuality"
            :disabled="isProgressing"
            size="small"
            style="width: 100px"
            @change="setImageQuality"
          >
            <el-option label="压缩" :value="1">压缩</el-option>
            <el-option label="不压缩" :value="2">不压缩</el-option>
          </el-select>
        </div>
        <el-upload
          ref="pdfUploader"
          class="pdf-uploader"
          accept=".pdf"
          action=""
          :auto-upload="false"
          drag
          :limit="1"
          :on-change="handleChange"
          :on-remove="handleRemove"
        >
          <template #trigger>
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
            <div class="el-upload__tip">只能上传pdf文件，且不超过100mb</div>
          </template>
        </el-upload>
        <div v-if="progressing.total > 0" class="progressing-bar d2-mt-8">
          {{ filename }}
          <template v-if="progressing.current === progressing.total">
            处理完成，共{{ progressing.total }}页
          </template>
          <template v-else>处理中：{{ progressing.current }}/{{ progressing.total }} 页</template>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.pdf-to-image__content {
  padding-bottom: 24px;
}
.pdf-uploader {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0 auto;
  ::v-deep {
    .el-upload-dragger {
      width: 100%;
    }
  }
}
.pdf-to-image__trigger {
  cursor: pointer;
  .el-icon {
    color: var(--color-primary-v3);
    font-size: 24px;
  }
}
</style>

<vue-filename-injector>
export default function (Component) {
  Component.options.__source = "src/views/data-center/components/PdfToImage/index.vue"
}
</vue-filename-injector>
